<template>
  <div
    ref="container"
    class="project-download"
    :class="{ 'is-open': dropdownIsOpen }"
  >
    <div v-if="getDownload.loading" class="c-actions__button">
      <span class="c-actions__label">{{ t('loading') }}</span>
    </div>

    <div v-else-if="getDownload.err" class="c-actions__button">
      <span class="c-actions__label">Error retrieving download</span>
    </div>

    <div v-else-if="!getDownload.data" class="c-actions__button">
      <span class="c-actions__label">
        Unexpected error from API: no data
      </span>
    </div>

    <!-- missing download links -->
    <div
      v-else-if="getDownload.data.links.length < 1"
      class="c-actions__button c-actions__button--disabled"
      href="#"
    >
      <SVGIcon class="c-actions__icon" name="download" />
      <span class="c-actions__label">
        {{ t('download') }}
        <small class="c-actions__informations">{{ t('noDownloadLinks') }}</small>
      </span>
      <SVGIcon class="c-actions__icon" name="download" />
    </div>

    <!-- user not logged in -->
    <a v-else-if="!isLogged" class="c-actions__button" :href="loginURL">
      <SVGIcon
        class="c-actions__icon"
        :name="getButtonIcon(getDownload.data.links[0])"
      />
      <span class="c-actions__label">
        {{
          getDownload.data.links[0].link_type === 'link' ?
            t('loginToPlay') :
            t('loginToDownload')
        }}
      </span>
      <SVGIcon
        class="c-actions__icon"
        :name="getButtonIcon(getDownload.data.links[0])"
      />
    </a>

    <!-- one link = direct link (no dropdown) -->
    <a
      v-else-if="getDownload.data.links.length === 1"
      class="c-actions__button"
      :href="getUrl(getDownload.data.links[0])"
      target="_blank"
      rel="noopener noreferrer"
    >
      <SVGIcon
        class="c-actions__icon"
        :name="getButtonIcon(getDownload.data.links[0])"
      />
      <span class="c-actions__label">
        {{
          getDownload.data.links[0].link_type === 'link' ?
            t('play') :
            t('download')
        }}
        <small v-if="getDownload.data.links[0].group" class="c-actions__informations">
          {{ getDownload.data.links[0].group }}
        </small>
      </span>
      <SVGIcon
        v-if="getDownload.data.links[0].authorized_download"
        class="c-actions__icon"
        :name="getButtonIcon(getDownload.data.links[0])"
      />
      <SVGIcon
        v-else-if="getDownload.data.links[0].protection_unlock_url"
        class="c-actions__icon" name="cart"
      />
      <SVGIcon v-else class="c-actions__icon" name="padlock" />
    </a>

    <!-- two or more links = dropdown -->
    <template v-else-if="getDownload.data.links.length > 1">
      <button
        type="button"
        class="c-actions__button"
        @click.prevent="dropdownIsOpen = !dropdownIsOpen"
      >
        <SVGIcon class="c-actions__icon u-color-invert" name="chevron-down" />
        <span class="c-actions__label">
          {{ t('download') }}
          <small
            v-if="getDownload.data.links[0].group"
            class="c-actions__informations"
          >{{ getDownload.data.links[0].group }}</small>
        </span>
        <SVGIcon class="c-actions__icon u-color-invert" name="chevron-down" />
      </button>

      <ul class="c-actions__files">
        <li
          v-for="link of getDownload.data.links"
          :key="link.url"
          class="c-actions__file"
        >
          <a
            :href="getUrl(link)"
            class="c-actions__link"
            target="_blank"
            :title="link.title"
            rel="noopener noreferrer"
          >
            <span class="c-actions__label">
              {{ link.title }}
              <small v-if="link.subtitle" class="c-actions__informations">
                {{ link.subtitle }}
              </small>
            </span>
            <SVGIcon
              v-if="getDownload.data.links[0].authorized_download"
              class="c-actions__icon" name="download"
            />
            <SVGIcon
              v-else-if="getDownload.data.links[0].protection_unlock_url"
              class="c-actions__icon" name="cart"
            />
            <SVGIcon v-else class="c-actions__icon" name="padlock" />
          </a>
        </li>
      </ul>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted, watchEffect } from 'vue'
import { useI18n } from '@/utils/i18n'
import { useAuth } from '@/utils/auth'
import { useAuthURL } from '@/utils/urls'
import { useDownloadContext } from '@/contexts/ProjectContext'
import { Link } from '@/utils/api'
import SVGIcon from '@/components/SVGIcon.vue'

const dropdownIsOpen = ref(false)

const { loginURL } = useAuthURL()
const getDownload = useDownloadContext()

const getUrl = (link: Link) => {
  if (!link.authorized_download) {
    return link.protection_unlock_url ?? undefined
  }
  return link.url!
}

const getButtonIcon = (link: Link) => {
  if (link.link_type === 'link') {
    return 'play'
  }
  return 'download'
}

const { t } = useI18n({
  messages: {
    fr: {
      loading: 'Chargement...',
      loginToDownload: 'Se connecter pour télécharger',
      loginToPlay: 'Se connecter pour jouer',
      play: 'Jouer',
      download: 'Télécharger',
      noDownloadLinks: "Aucun lien de téléchargement n'a été trouvé pour ce projet"
    },
    en: {
      loading: 'Loading...',
      loginToDownload: 'Login to download',
      loginToPlay: 'Login to play',
      play: 'Play',
      download: 'Download',
      noDownloadLinks: 'No download links found for this project'
    }
  }
})

const { isLogged } = useAuth()

// close file list when user click outside of button
function clickOutside (ev: MouseEvent) {
  const target = ev.target as HTMLElement
  // if user clicked on the button
  // if user clicked on a button's child
  // we don't close the dropdown
  if (target === container.value || container.value!.contains(target)) {
    return
  }
  dropdownIsOpen.value = false
}

const container = ref<HTMLDivElement>()
onMounted(() => {
  watchEffect(() => {
    if (dropdownIsOpen.value) {
      document.addEventListener('click', clickOutside)
    } else {
      document.removeEventListener('click', clickOutside)
    }
  })
})

onUnmounted(() => { document.removeEventListener('click', clickOutside) })
</script>

<style lang="scss" scoped>
@use "sass-rem" as rem;
@use "@/sass/settings";
@use "@/sass/helpers/mq";
@use "@/sass/helpers/font";
@use "@/sass/helpers/size";

.c-actions__button {
  background: settings.$color-primary;
  border-radius: rem.convert(2px 0 0 2px);
  color: settings.$color-background;
  position: relative;
  text-align: left;

  &::before {
    background: settings.$color-background;
    content: "";
    opacity: 0;
    transition: opacity 400ms;
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  &:hover {
    color: settings.$color-background;

    &::before {
      opacity: .25;
    }
  }
}

.c-actions_download-container .project-download {
  max-width: 100%;
}

/* flip icon */
.project-download.is-open .c-actions__button {
  .c-actions__icon:last-child {
    transform: scaleY(-1);
  }
}

@include mq.mq($until: medium) {
  .c-actions__button {
    flex-direction: row;

    .c-actions__label {
      margin-top: 0;
      margin-left: 0.5rem;
      font-size: rem.convert(12px);
    }
  }
  .c-actions__button .c-actions__icon:last-child {
    display: none;
    @include size.size(16px);
    margin: auto rem.convert(4px);
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
  }
}

@include mq.mq($from: medium) {
  .c-actions__button {
    flex-direction: row;
    border-radius: rem.convert(2px);
    padding: rem.convert(16px);

    .c-actions__icon {
      @include size.size(24px);

      &:first-child {
        display: none;
        margin-right: rem.convert(8px);
      }
    }

    .c-actions__label {
      flex: 1 1 auto;
      font-family: settings.$font-primary;
      font-size: rem.convert(24px);
      margin: 0;
    }

    .c-actions__informations {
      display: block;
      @include font.size(14px, 16px);
    }
  }
}

.c-actions__files {
  width: auto;
  background: settings.$color-background;
  box-shadow: 0 rem.convert(16px 24px) 0 rgba(settings.$color-foreground, .1);
  border-radius: rem.convert(2px);
  margin-top: rem.convert(8px);
  position: absolute;
  top: 100%;
  right: 0;
  left: 0;
  max-height: 0;
  opacity: 0;
  overflow: hidden;
  pointer-events: none;
  transform: translateY(rem.convert(-8px));
  transition: max-height 0ms 400ms, opacity 400ms, transform 400ms;

  .project-download.is-open & {
    opacity: 1;
    pointer-events: auto;
    max-height: 999px;
    max-height: 100vh;
    max-height: calc(100vh - #{rem.convert(80px)});
    transform: translateY(0rem);
    transition-delay: 0ms;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
  }
}

.c-actions__file {
  position: relative;

  & + &::before {
    border-top: 1px solid settings.$color-lightergrey;
    content: "";
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
  }
}

@include mq.mq($from: medium) {
  .c-actions__files {
    margin: 0 rem.convert(8px);
  }

  .c-actions__file + .c-actions__file::before {
    margin: 0 rem.convert(16px);
  }
}

@include mq.mq($from: large) {
  .c-actions__files {
    margin: rem.convert(-4px 12px) 0;
  }
}
</style>
